import { ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { ScheduleItem } from './useTodayBoardData'
import { formatDate } from '@/lib/utils/format-business-data'
import {
  SCHEDULE_CONFIDENCE_LABELS,
  SCHEDULE_CONFIDENCE_VARIANTS,
  SCHEDULE_SOURCE_LABELS,
  SCHEDULE_SOURCE_VARIANTS,
} from '@/types/aircraft-schedules'

interface ScheduleRowProps {
  item: ScheduleItem
  /** When set, shows movement icon. Omit in column views where context is clear. */
  showMovementIcon?: boolean
  /** Whole row click */
  onRowClick?: (item: ScheduleItem) => void
  /** Check In button click (arriving aircraft) */
  onCheckIn?: (item: ScheduleItem) => void
  /** Check Out button click (departing aircraft) */
  onCheckOut?: (item: ScheduleItem) => void
  /** Register button click (arriving aircraft not yet set up) */
  onRegister?: (item: ScheduleItem) => void
}

const TRIAGE_DOT_CLASS: Record<string, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
}

function getTriageDot(item: ScheduleItem) {
  // Prefer the injected demo triage color; fall back to the warning dot.
  if (item.triageColor && TRIAGE_DOT_CLASS[item.triageColor]) {
    return (
      <span
        className={`h-2 w-2 rounded-full shrink-0 ${TRIAGE_DOT_CLASS[item.triageColor]}`}
        title={`Triage: ${item.triageColor}${item.triageCondition ? ` — ${item.triageCondition.replace(/_/g, ' ')}` : ''}`}
      />
    )
  }
  if (item.hasWarnings) return <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
  return null
}

function getClientName(data: ScheduleItem['data']): string | null {
  if (data.leaseContract?.clientName) return data.leaseContract.clientName
  if (data.transientStay?.clientName) return data.transientStay.clientName
  if (data.aircraft.owner?.companyName) return data.aircraft.owner.companyName
  if (data.aircraft.ownerName) return data.aircraft.ownerName
  return null
}

function getTimeDisplay(item: ScheduleItem): string | null {
  // Schedule-sourced: show HH:mm (same-day context — full date is redundant)
  if (item.scheduledTime) {
    const d = new Date(item.scheduledTime)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }
  if (!item.sortTime) return null
  return formatDate(item.sortTime)
}

function MovementIcon({ type }: { type: 'arriving' | 'departing' }) {
  if (type === 'arriving') {
    return <ArrowDownToLine className="h-3.5 w-3.5 text-blue-600 shrink-0" />
  }
  return <ArrowUpFromLine className="h-3.5 w-3.5 text-orange-600 shrink-0" />
}

export function ScheduleRow({ item, showMovementIcon, onRowClick, onCheckIn, onCheckOut }: ScheduleRowProps) {
  const { data } = item
  const clientName = getClientName(data)
  const timeDisplay = getTimeDisplay(item)
  const isCompleted = item.scheduleStatus === 'completed'

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 hover:bg-accent/50 rounded-md transition-colors group cursor-pointer ${isCompleted ? 'opacity-50' : ''}`}
      onClick={() => onRowClick?.(item)}
    >
      {showMovementIcon && <MovementIcon type={item.movementType} />}
      {getTriageDot(item)}

      <span className={`font-mono text-sm font-medium w-20 shrink-0 ${isCompleted ? 'line-through' : ''}`}>
        {data.aircraft.tailNumber}
      </span>
      <span className="text-xs text-muted-foreground shrink-0">
        {data.aircraft.typeCode}
      </span>

      {clientName && (
        <>
          <span className="text-muted-foreground text-xs">·</span>
          <span className="text-xs text-muted-foreground truncate max-w-[140px]">
            {clientName}
          </span>
        </>
      )}

      {/* Airport badge (schedule-sourced) */}
      {item.airportIcao && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-mono shrink-0">
              {item.airportIcao}
            </Badge>
          </TooltipTrigger>
          {item.airportName && (
            <TooltipContent side="top" className="text-xs">
              {item.airportName}
            </TooltipContent>
          )}
        </Tooltip>
      )}

      {/* Confidence badge — only show if NOT confirmed (tentative/predicted) */}
      {item.confidence && item.confidence !== 'confirmed' && (
        <Badge
          variant={SCHEDULE_CONFIDENCE_VARIANTS[item.confidence]}
          className="text-[9px] h-4 px-1"
        >
          {SCHEDULE_CONFIDENCE_LABELS[item.confidence]}
        </Badge>
      )}

      {/* Source badge — only show if NOT csr_entered (noteworthy external sources) */}
      {item.source && item.source !== 'csr_entered' && (
        <Badge
          variant={SCHEDULE_SOURCE_VARIANTS[item.source]}
          className="text-[9px] h-4 px-1"
        >
          {SCHEDULE_SOURCE_LABELS[item.source]}
        </Badge>
      )}

      <div className="ml-auto flex items-center gap-2 shrink-0">
        {timeDisplay && (
          <span className={`text-xs ${item.scheduledTime ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
            {timeDisplay}
          </span>
        )}

        {item.isInAttentionFeed && (
          <Badge variant="outline" className="text-[9px] h-4 px-1 text-muted-foreground">
            See above
          </Badge>
        )}

        {/* Hide action buttons for completed schedules */}
        {!isCompleted && item.movementType === 'arriving' && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[11px] px-2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => { e.stopPropagation(); onCheckIn?.(item) }}
          >
            Check In
          </Button>
        )}
        {!isCompleted && item.movementType === 'departing' && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[11px] px-2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => { e.stopPropagation(); onCheckOut?.(item) }}
          >
            Check Out
          </Button>
        )}
      </div>
    </div>
  )
}
